/* ============================================================================
   CABINA — el armazón que envuelve todas las herramientas.

   No calcula nada. Recibe el estado ya resuelto —la lista de pestañas, los
   datos de la franja, los avisos— y lo muestra. Todo el cálculo sigue donde
   estaba.

   Por qué existe: hasta ahora la navegación era una fila de botones con el
   nombre largo de cada pestaña, y cada alerta vivía adentro de su pestaña. En
   un teléfono eso significa dos líneas de botones antes de ver nada, y para
   saber si el vuelo está en condiciones hay que entrar a las cinco.
   ========================================================================== */

import { SELLO_PROPIO } from "./selloDeVersion.js";
import { TEMAS } from "../domain/temaVisual.js";
import React, { useEffect, useState } from "react";

import Icono from "./iconos/IconosHeliCalc.jsx";
import { estadoMaster } from "../domain/avisos.js";
import "../styles/cabina.css";

const CLASE_AVISO = {
  alarma: "hc-aviso--alarma",
  precaucion: "hc-aviso--precaucion",
  aviso: "hc-aviso--aviso",
  normal: "hc-aviso--normal",
};

const CLASE_PUNTO = {
  alarma: "hc-punto--alarma",
  precaucion: "hc-punto--precaucion",
};

/** Una celda del bloque de datos. */
export function DatoDeFranja({ rotulo, valor, unidad, estado }) {
  return (
    <div className="hc-franja__celda">
      <span className="hc-rotulo">{rotulo}</span>
      <span className={`hc-dato${estado ? ` hc-dato--${estado}` : ""}`} title={`${valor}${unidad ? ` ${unidad}` : ""}`}>
        {valor}
        {unidad ? <span className="hc-dato__unidad">{unidad}</span> : null}
      </span>
    </div>
  );
}

function ListaDeAvisos({ avisos, alIr }) {
  if (avisos.length === 0) {
    return (
      <div className="hc-avisos__lista">
        <p className="hc-avisos__vacio">
          Sin avisos abiertos. Los que aparezcan acá vienen de las herramientas:
          peso, performance, combustible, mantenimiento y riesgo.
        </p>
      </div>
    );
  }
  return (
    <div className="hc-avisos__lista">
      {avisos.map((a) => (
        <button
          key={a.codigo}
          type="button"
          className={`hc-aviso ${CLASE_AVISO[a.nivel] || ""}`}
          onClick={() => alIr?.(a.tab)}
        >
          <span className="hc-aviso__texto">
            {a.texto}
            <span className="hc-aviso__origen">{a.origen}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * Alto de la barra pegajosa del aviso legal.
 *
 * El aviso legal es `position: sticky; top: 0; z-index: 900` y no se puede
 * cerrar — es una decisión del proyecto. La franja de datos también se pega
 * arriba, así que sin este ajuste quedaría DEBAJO del aviso y tapada.
 *
 * No sirve un número fijo: la barra cambia de alto al desplegarse el texto
 * completo y al rotar el teléfono. Se mide en vivo y se publica como variable
 * CSS, que es lo que usan la franja, el riel y el panel de avisos.
 */
function useAltoDeBarras() {
  useEffect(() => {
    const raiz = document.documentElement;
    const barra = document.querySelector(".hc-bar--disclaimer");
    if (!barra) {
      raiz.style.setProperty("--hc-alto-barras", "0px");
      return undefined;
    }
    const medir = () => {
      raiz.style.setProperty("--hc-alto-barras", `${Math.ceil(barra.getBoundingClientRect().height)}px`);
    };
    medir();
    // Sin ResizeObserver (navegadores viejos) queda la medida inicial, que ya
    // es mejor que un número inventado.
    if (typeof ResizeObserver === "undefined") return undefined;
    const observador = new ResizeObserver(medir);
    observador.observe(barra);
    return () => observador.disconnect();
  }, []);
}

export default function Cabina({
  herramientas = [],
  activa,
  alCambiar,
  matricula = "—",
  tema = "auto",
  onTema,
  tipo = "",
  datos = [],
  avisos = [],
  panelVisible = true,
  alCambiarPanel,
  gruposCerrados = [],
  alCambiarGrupo,
  children,
}) {
  const [reconocido, setReconocido] = useState(false);
  const [desplegado, setDesplegado] = useState(false);
  const [cajon, setCajon] = useState(false);

  useAltoDeBarras();

  const master = estadoMaster(avisos);

  // Un aviso nuevo vuelve a encender el máster aunque ya se hubiera
  // reconocido el anterior: si no, una alarma que aparece después de tocar el
  // botón entra en silencio.
  const firma = avisos.map((a) => `${a.nivel}:${a.codigo}`).join("|");
  useEffect(() => { setReconocido(false); }, [firma]);

  const claseMaster = master === "apagado" || reconocido
    ? "hc-master--reconocido"
    : `hc-master--${master}`;

  // La herramienta que generó el aviso más grave, para el punto del riel.
  const peorPorTab = {};
  for (const a of avisos) {
    if (!CLASE_PUNTO[a.nivel]) continue;
    if (peorPorTab[a.tab] !== "alarma") peorPorTab[a.tab] = a.nivel;
  }

  const irA = (tab) => {
    if (tab) alCambiar?.(tab);
    setDesplegado(false);
  };

  /* ═══ LAS PANTALLAS QUE NO SON HERRAMIENTAS DE VUELO ═══

     La administración de empresa, usuarios y perfiles no se lista acá: se entra
     desde la barra de arriba, que es la única que está a la vista en TODAS las
     pantallas. Listada en el riel quedaba DUPLICADA —el riel la muestra cuando
     entra, el cajón «Todas» cuando no— y a 883 px, que es un iPad en
     horizontal, directamente no se veía.

     Una sola puerta, y arriba. */
  const herramientasDelRiel = herramientas.filter((h) => !h.fueraDeLaLista);

  const grupos = [];
  for (const h of herramientasDelRiel) {
    const ultimo = grupos.at(-1);
    if (ultimo && ultimo.nombre === h.grupo) ultimo.items.push(h);
    else grupos.push({ nombre: h.grupo, items: [h] });
  }

  const botonMaster = (etiqueta, alTocar) => (
    <button
      type="button"
      className={`hc-master ${claseMaster}`}
      onClick={alTocar}
      aria-label={`${avisos.length} aviso(s) — ${etiqueta}`}
    >
      <Icono nombre="aviso" tam={14} />
      {etiqueta}
    </button>
  );

  return (
    <div className="hc-cabina">

      <nav className="hc-riel" aria-label="Herramientas">
        <img className="hc-riel__marca" src="/iconos/icon-192.png" alt="HeliCalc" />
        {/* ═══ LOS GRUPOS SE PLIEGAN ═══

            Trece herramientas en dos categorías llenan el riel de un portátil y
            obligan a desplazarlo para llegar a las últimas. Plegadas, el menú
            entra entero y cada uno deja abierto lo que usa.

            El encabezado es un botón, y cuando el grupo está plegado CON la
            herramienta activa adentro se marca: si no, al cambiar de pantalla
            el riel se queda sin decir dónde se está parado. */}
        {grupos.map((g) => {
          const clave = g.nombre || "sin-grupo";
          const cerrado = Boolean(g.nombre) && gruposCerrados.includes(g.nombre);
          const tieneActiva = g.items.some((h) => h.key === activa);
          return (
            <React.Fragment key={clave}>
              {g.nombre ? (
                <button
                  type="button"
                  className="hc-riel__grupo"
                  data-activa={cerrado && tieneActiva ? "si" : undefined}
                  aria-expanded={!cerrado}
                  title={cerrado ? `Mostrar ${g.nombre}` : `Ocultar ${g.nombre}`}
                  onClick={() => alCambiarGrupo?.(g.nombre)}
                >
                  <span>{g.nombre}</span>
                  <span className="hc-riel__flecha" aria-hidden="true">{cerrado ? "▸" : "▾"}</span>
                </button>
              ) : null}
              {cerrado ? null : g.items.map((h) => (
                <button
                  key={h.key}
                  type="button"
                  className="hc-riel__btn"
                  aria-current={h.key === activa ? "page" : undefined}
                  onClick={() => alCambiar?.(h.key)}
                >
                  {peorPorTab[h.key] ? (
                    <i className={`hc-riel__punto ${CLASE_PUNTO[peorPorTab[h.key]]}`} aria-hidden="true" />
                  ) : null}
                  <Icono nombre={h.key} tam={22} />
                  <span>{h.corto}</span>
                </button>
              ))}
            </React.Fragment>
          );
        })}
      </nav>

      <div className="hc-cabina__centro">
        <header className="hc-franja">
          <div className="hc-franja__aeronave">
            <Icono nombre="aeronave" tam={22} />
            <div>
              <div className="hc-rotulo">{tipo || "Aeronave"}</div>
              <span className="hc-dato">{matricula}</span>
              {/* El sello de la compilación, chiquito y siempre a la vista.
                  Sin esto, una captura no dice qué versión está corriendo el
                  equipo, y «no funciona» se confunde con «estás viendo otra
                  cosa». Costó muchas rondas averiguarlo. */}
              <span className="hc-franja__sello" title="Compilación en uso">{SELLO_PROPIO}</span>
              {/* Día/noche. En la franja y no en un menú: en vuelo nocturno
                  hace falta poder cambiarlo sin buscar dónde está. */}
              {onTema && (
                <select
                  className="hc-franja__tema"
                  value={tema}
                  onChange={(e) => onTema(e.target.value)}
                  aria-label="Tema visual"
                  title="Tema visual: de noche, sin azul y con brillo bajo"
                >
                  {TEMAS.map(([clave, nombre]) => (
                    <option key={clave} value={clave}>{nombre}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
          {datos.map((d) => <DatoDeFranja key={d.rotulo} {...d} />)}
          {botonMaster(String(avisos.length), () => {
            setReconocido(true);
            setDesplegado((v) => !v);
          })}
          {desplegado ? (
            <div className="hc-avisos hc-avisos--desplegado">
              <ListaDeAvisos avisos={avisos} alIr={irA} />
            </div>
          ) : null}
        </header>

        {children}
      </div>

      {panelVisible ? (
        <aside className="hc-avisos" aria-label="Avisos">
          <div className="hc-avisos__cabecera">
            <span className="hc-rotulo">Avisos</span>
            {botonMaster("Reconocer", () => setReconocido(true))}
            <button
              type="button"
              className="hc-avisos__cerrar"
              onClick={() => alCambiarPanel?.(false)}
              aria-label="Ocultar el panel de avisos"
              title="Ocultar el panel de avisos"
            >
              ✕
            </button>
          </div>
          <ListaDeAvisos avisos={avisos} alIr={irA} />
        </aside>
      ) : (
        /* Cerrado no significa apagado: la pestaña sigue avisando por su punto
           en el riel y por el máster de la franja. Lo que se oculta es la
           lista, no la información — un panel que se puede apagar del todo
           deja de servir el día que hace falta. */
        <button
          type="button"
          className={`hc-avisos-lengueta ${claseMaster}`}
          onClick={() => alCambiarPanel?.(true)}
          aria-label={`Mostrar el panel de avisos (${avisos.length})`}
          title="Mostrar el panel de avisos"
        >
          <Icono nombre="aviso" tam={16} />
          <span className="hc-avisos-lengueta__n">{avisos.length}</span>
          <span className="hc-avisos-lengueta__texto">Avisos</span>
        </button>
      )}

      <nav className="hc-consola" aria-label="Acceso rápido">
        {herramientasDelRiel.filter((h) => h.enConsola).slice(0, 4).map((h) => (
          <button
            key={h.key}
            type="button"
            className="hc-consola__btn"
            aria-current={h.key === activa ? "page" : undefined}
            onClick={() => { setCajon(false); alCambiar?.(h.key); }}
          >
            <Icono nombre={h.key} tam={22} />
            <span>{h.corto}</span>
          </button>
        ))}
        <button
          type="button"
          className="hc-consola__btn"
          aria-expanded={cajon}
          onClick={() => setCajon((v) => !v)}
        >
          <Icono nombre="dashboard" tam={22} />
          <span>{cajon ? "Cerrar" : "Todas"}</span>
        </button>
      </nav>

      {/* En el borde inferior entran cuatro herramientas; las once no. El
          cajón las muestra todas, POR CATEGORÍA: once botones sueltos en una
          rejilla no dicen a qué parte de la aplicación pertenece cada uno, y
          una pestaña a la que no se llega desde el teléfono es una pestaña que
          no existe. */}
      {cajon ? (
        <div className="hc-cajon" role="dialog" aria-label="Todas las herramientas">
          {grupos.map((g) => (
            <div className="hc-cajon__seccion" key={g.nombre || "sin-grupo"}>
              {g.nombre ? <div className="hc-cajon__grupo">{g.nombre}</div> : null}
              <div className="hc-cajon__rejilla">
                {g.items.map((h) => (
                  <button
                    key={h.key}
                    type="button"
                    className="hc-cajon__btn"
                    aria-current={h.key === activa ? "page" : undefined}
                    onClick={() => { setCajon(false); alCambiar?.(h.key); }}
                  >
                    {peorPorTab[h.key] ? (
                      <i className={`hc-riel__punto ${CLASE_PUNTO[peorPorTab[h.key]]}`} aria-hidden="true" />
                    ) : null}
                    <Icono nombre={h.key} tam={24} />
                    <span>{h.corto}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}

    </div>
  );
}
